import {
  useContext,
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import { notification } from "antd";
import queryClient from "../queryClient";
import { getTokenExpiryDelayMs, getTimeoutDelayMs } from "../utils/authSession";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const pickSelectedProject = (user, currentProject = null) => {
  const stillCurrent =
    currentProject &&
    user?.projects?.find((p) => p.project_id === currentProject.project_id);
  if (stillCurrent) {
    return stillCurrent;
  }
  const savedProject = readJson("selectedProject");
  if (
    savedProject &&
    user?.projects?.find((p) => p.project_id === savedProject.project_id)
  ) {
    return savedProject;
  }
  if (user?.projects?.length > 0) {
    return user.projects.find((p) => !p.archived) || user.projects[0];
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProject, _setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [magicLoginActive, setMagicLoginActive] = useState(false);
  const expiryTimerRef = useRef(null);
  const expiryTokenRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    expiryTokenRef.current = null;
  }, []);

  const logout = useCallback(() => {
    clearExpiryTimer();
    sessionTokenRef.current = null;
    setCurrentUser(null);
    _setSelectedProject(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("selectedProject");
    // Drop the previous session's cache before any still-mounted query refetches.
    queryClient.clear();
  }, [clearExpiryTimer]);

  const expireCurrentSession = useCallback(
    (token) => {
      if (localStorage.getItem("token") !== token) return;
      logout();
      notification.error({
        message: "Session Expired",
        description: "Your session has expired. Please log in again.",
      });
    },
    [logout]
  );

  const startTokenExpiryCheck = useCallback(
    (token) => {
      clearExpiryTimer();
      if (!token) return;

      let decodedToken;
      try {
        decodedToken = jwtDecode(token);
      } catch (_) {
        logout();
        return;
      }

      const scheduleExpiry = () => {
        if (localStorage.getItem("token") !== token) return;

        const delay = getTokenExpiryDelayMs(decodedToken.exp);
        if (!decodedToken.exp || delay <= 0) {
          expireCurrentSession(token);
          return;
        }

        expiryTokenRef.current = token;
        expiryTimerRef.current = setTimeout(() => {
          if (localStorage.getItem("token") !== token) return;
          const remaining = getTokenExpiryDelayMs(decodedToken.exp);
          if (remaining <= 0) {
            expireCurrentSession(token);
          } else {
            scheduleExpiry();
          }
        }, getTimeoutDelayMs(delay));
      };

      scheduleExpiry();
    },
    [clearExpiryTimer, expireCurrentSession, logout]
  );

  const login = useCallback(
    (user, token, projectId = null) => {
      setCurrentUser(user);
      const firstActiveProject =
        user.projects.find((p) => !p.archived) || user.projects[0];
      let nextProject = null;
      if (projectId) {
        nextProject = user.projects.find((p) => p.project_id === projectId);
      }
      _setSelectedProject(nextProject || firstActiveProject);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", token);
      sessionTokenRef.current = token;
      startTokenExpiryCheck(token);
    },
    [startTokenExpiryCheck]
  );

  const setSelectedProject = (project) => {
    _setSelectedProject(project);
    if (project) {
      localStorage.setItem("selectedProject", JSON.stringify(project));
    }
  };

  useEffect(() => {
    const restoreFromStorage = () => {
      const user = readJson("user");
      const token = localStorage.getItem("token");
      if (user && token) {
        if (sessionTokenRef.current && sessionTokenRef.current !== token) {
          queryClient.clear();
        }
        sessionTokenRef.current = token;
        setCurrentUser(user);
        _setSelectedProject((prev) => pickSelectedProject(user, prev));
        startTokenExpiryCheck(token);
      } else {
        clearExpiryTimer();
        sessionTokenRef.current = null;
        setCurrentUser(null);
        _setSelectedProject(null);
        queryClient.clear();
      }
    };

    restoreFromStorage();
    setLoading(false);

    const onStorage = (event) => {
      if (event.key && event.key !== "token" && event.key !== "user") {
        return;
      }
      restoreFromStorage();
    };

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearExpiryTimer();
    };
  }, [clearExpiryTimer, startTokenExpiryCheck]);

  const value = {
    currentUser,
    selectedProject,
    setSelectedProject,
    setCurrentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    magicLoginActive,
    setMagicLoginActive,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
